import React from "react";

import { css, keyframes } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives";

import { Picture, PictureProps } from "@/components/wpl/image/src/index";
import Link, { LinkProps } from "@/components/wpl/link/src";
import {
    ProductKeyTypes,
    Row,
    TypographyStyles
} from "@/libraries/wpl/foundations/src";

interface ContentList {
    headerText: string;
    headerType?: string;
    bodyText: string;
}

interface TabContentLinkProps {
    linkText: string;
    link: string;
    linkLabel: string;
    productKey: ProductKeyTypes;
}

export interface TabContentProps {
    contentList: ContentList[];
    contentLink?: TabContentLinkProps;
    pictureProps: PictureProps;
    tabContentEyebrowText?: string;
    cardsContent: CardContentProps[];
    className?: string;
}

const TabsContent = (tabsContentProps: TabContentProps) => {
    return (
        <TypographyStyles>
            <div
                css={wrapperStyles}
                className={tabsContentProps.className || ""}
            >
                <Row>
                    <div css={contentListWrapperStyle}>
                        <div css={contentListTextWrapperStyle}>
                            <div
                                css={contentListStyles}
                                className="content-list"
                            >
                                {tabsContentProps.contentList.map(
                                    (content, index) => (
                                        <div
                                            key={"content-" + index}
                                            css={ContentTextStyles}
                                            className="content-text"
                                        >
                                            <h3
                                                css={[contentListHeaderStyle]}
                                                className={content?.headerType}
                                            >
                                                {content.headerText}
                                            </h3>
                                            <p css={ContentTextPStyle}>
                                                {content.bodyText}
                                            </p>
                                        </div>
                                    )
                                )}
                            </div>
                            {tabsContentProps.contentLink && (
                                <div
                                    css={linkContainerStyle}
                                    className="link-content-container"
                                >
                                    <Link
                                        href={tabsContentProps.contentLink.link}
                                        text={
                                            tabsContentProps.contentLink
                                                ?.linkText
                                        }
                                        type={"basic"}
                                        doesArrowAnimate={true}
                                        dataEventProps={{
                                            "data-product-key":
                                                tabsContentProps.contentLink
                                                    ?.productKey,
                                            "data-event": "clicked",
                                            "data-event-component":
                                                "linkButton",
                                            "data-event-container":
                                                "tabsSection",
                                            "data-label-english":
                                                tabsContentProps.contentLink
                                                    ?.linkLabel
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <div css={bigImgWrapperStyles}>
                            <Picture
                                {...tabsContentProps.pictureProps}
                                css={contentImageStyle}
                            />
                        </div>
                    </div>
                </Row>
                {tabsContentProps.tabContentEyebrowText && (
                    <Row>
                        <div className="tab-content-header-wrapper">
                            <h4 className="eyebrow-md" css={h4Style}>
                                {tabsContentProps.tabContentEyebrowText}
                            </h4>
                        </div>
                    </Row>
                )}
                <div className="tabs-content-cards">
                    <Row layout="4-4-4">
                        {tabsContentProps.cardsContent.map(
                            (cardContent, index) => (
                                <div key={"card-" + index}>
                                    <TabCard {...cardContent} />
                                </div>
                            )
                        )}
                    </Row>
                </div>
            </div>
        </TypographyStyles>
    );
};

const slidein = keyframes({
    from: {
        opacity: 0,
        transform: "translateX(-100px)"
    },
    to: {
        opacity: 1,
        transform: "translateX(0)"
    }
});

const wrapperStyles = css({
    animation: `${slidein} linear 500ms 1`
});

const bigImgWrapperStyles = css({
    overflow: "hidden",

    [media.above.md]: {
        gridColumnStart: 5,
        gridColumnEnd: -1
    },
    [media.below.md]: {
        marginBlockStart: 20
    }
});

const contentListWrapperStyle = css({
    [media.above.md]: {
        display: "grid",
        gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
        gap: 32
    }
});

const contentListTextWrapperStyle = css({
    [media.above.md]: {
        gridColumnStart: 1,
        gridColumnEnd: 5
    }
});

const contentListStyles = css({
    [media.only.sm]: {
        display: "flex",
        gap: "32px"
    }
});

const contentImageStyle = css({
    maxWidth: "100%",
    objectFit: "contain",
    width: "100%",
    alignSelf: "baseline",
    height: "auto",
    animation: `${slidein} linear 500ms 1`,

    [media.below.sm]: {
        marginBlockStart: "6px"
    }
});

const h4Style = css({
    textAlign: "center",
    marginBlockStart: 0,
    marginBlockEnd: 24
});

const ContentTextStyles = css({
    [media.below.sm]: {
        paddingBlockEnd: 16
    },
    [media.above.md]: {
        "paddingBlockEnd": 8,
        "marginBlockEnd": 24,
        "borderBlockEnd": "1.5px dashed #B3BAC5",
        "&:only-of-type": {
            borderBlockEnd: "none"
        }
    }
});

const ContentTextPStyle = css({
    [media.below.sm]: {
        marginBlockStart: 8
    }
});

const contentListHeaderStyle = css({
    marginBlockEnd: 0,
    marginBlockStart: 0
});

const linkContainerStyle = css({
    [media.only.sm]: {
        textAlign: "center"
    }
});

interface CardContentProps {
    eyebrowText?: string;
    headerText: string | React.ReactElement<typeof FormattedMessage>;
    bodyText?: string;
    clickableCardUrl: string;
    ctaProps?: LinkProps;
    pictureProps: PictureProps;
    className?: string;
}

const TabCard = (cardContentProps: CardContentProps) => {
    const {
        eyebrowText,
        headerText,
        bodyText,
        clickableCardUrl,
        ctaProps,
        pictureProps,
        className
    } = cardContentProps;

    const handleClick = () => {
        if (clickableCardUrl) {
            window.location.href = clickableCardUrl;
        }
    };
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key == "Enter") {
            handleClick();
        }
    };
    const clickableCardStyle = css({
        cursor: `${clickableCardUrl ? "pointer" : "default"}`
    });

    return (
        <div
            className={["tabs-content-card", className].join(" ")}
            css={[tabsContentCardStyle, clickableCardStyle]}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            <div className="img-section">
                <Picture {...pictureProps} css={tabCardImgStyle} />
            </div>

            <div css={textSectionStyle} className="text-section">
                <h4 css={tabCardh4Style}>{headerText}</h4>
                {eyebrowText && (
                    <h5 css={tabCardEyebrowStyle} className="eyebrow-md">
                        {eyebrowText}
                    </h5>
                )}

                {bodyText && (
                    <p css={tabCardPStyle} className="contentBlock-body">
                        {bodyText}
                    </p>
                )}

                {ctaProps && <Link {...ctaProps} />}
            </div>
        </div>
    );
};

const tabsContentCardStyle = css({
    "borderRadius": "10px",
    "height": "100%",
    "overflow": "hidden",
    "boxShadow": "0 0 1px 0 rgba(9,30,66,.31),0 1px 1px 0 rgba(9,30,66,.25)",
    "transition": "box-shadow 0.2s ease-in-out",

    ":hover": {
        boxShadow: "0 8px 9pt 0 #091e4226,0 0 1px 0 #091e424f"
    }
});

const tabCardh4Style = css({
    margin: 0
});

const tabCardEyebrowStyle = css({
    order: -1,
    margin: 0
});

const tabCardPStyle = css({
    margin: 0
});

const tabCardImgStyle = css({
    height: "auto"
});

const textSectionStyle = css({
    padding: 24,
    display: "flex",
    flexDirection: "column",
    gap: 8
});

export default TabsContent;
